import React from "react";
import PropTypes from "prop-types";
import { GroupLabel } from "./GroupLabel";
import { GroupListing } from "./GroupListing";
import { SlideBlockContext } from "../SlideBlock";
import { Utilities } from "@learningblocks/exp-constants";
import { GroupWrapper } from "./GroupWrapper";

const { SLIDE_NODE } = Utilities;
const BOX_GROUP = {
  DEFAULT: "boxGroup",
  VISITED: "boxGroup-visited",
};

const getWrapperClass = (visited) =>
  visited ? BOX_GROUP.VISITED : BOX_GROUP.DEFAULT;

/**
 * BoxGroup-WithLabel: Used in How-to-make-vaccine Simulation
 *  - Component House the RectDragDrop Components/ HexDragDrop component typically placed inside a box
 */
function WithLabel(props) {
  const { getTabIndex } = React.useContext(SlideBlockContext);

  const className = getWrapperClass(props.visited);

  return (
    <GroupWrapper tabIndex={getTabIndex()} className={className}>
      <GroupLabel
        title={props.groupLabel}
        wrapClass={className}
        color={props.groupColor}
      />
      <GroupListing
        groupNodes={props.groupNodes}
        label={props.groupTitle}
        wrapClass={className}
        color={props.groupColor}
        rectDragDropRow={props.rectDragDropRow}
        jointDragDropRow={props.jointDragDropRow}
      />
    </GroupWrapper>
  );
}

WithLabel.propTypes = {
  /**
   * Unique Id for the group
   */
  groupId: PropTypes.number.isRequired,
  /**
   * List of all Components to be displayed inside the Group
   */
  groupNodes: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Booelean for specifying if current node.
       */
      current: PropTypes.bool,
      /**
       * List of components to be displayed in GroupRow
       */
      nodeComponents: PropTypes.array,
      /**
       * Object contains error messages for incorrect drops,
       * based on position of element
       */
      nodeFeedback: PropTypes.object,
      /**
       * Unique id for the Node
       */
      nodeId: PropTypes.number.isRequired,
      /**
       * The type of node.
       */
      nodeType: PropTypes.oneOf([
        SLIDE_NODE.JOINT_DRAG_DROP,
        SLIDE_NODE.BOX_DRAG_DROP,
      ]).isRequired,
    })
  ),
  /**
   * Title of the BoxGroup; It is displayed outisde the box
   * in bold letters  at top-left corner
   */
  groupLabel: PropTypes.string.isRequired,
  /**
   * The hexvalue for the colour associated with the box
   * group
   */
  groupColor: PropTypes.string,
  /**
   * Label String to be always displayed inside the Box
   */
  groupTitle: PropTypes.string,
  /**
   * Boolean specifying if BoxGroup is visited
   */
  visited: PropTypes.bool.isRequired,
  /**
   * DragDrop Row component for FullWidthCard nodes
   */
  rectDragDropRow: PropTypes.element.isRequired,
  /**
   * DragDropRow component for HexagonCard nodes
   */
  jointDragDropRow: PropTypes.element.isRequired,
};

WithLabel.defaultProsps = {
  groupId: null,
  groupNodes: [],
  visited: false,
};

/**
 * BoxGroup-WithoutLabel: Used in verify-snp-sequence Simulation
 *  - Component House the RectDragDrop Components/ HexDragDrop component typically placed inside a box
 */
function WithoutLabel(props) {
  const className = getWrapperClass(props.visited);

  return (
    <GroupWrapper className={className}>
      <GroupListing
        groupNodes={props.groupNodes}
        label={props.groupTitle}
        wrapClass={className}
        color={props.groupColor}
        rectDragDropRow={props.rectDragDropRow}
        jointDragDropRow={props.jointDragDropRow}
      />
    </GroupWrapper>
  );
}

WithoutLabel.propTypes = {
  /**
   * Unique Id for the group
   */
  groupId: PropTypes.number.isRequired,
  /**
   * List of all Components to be displayed inside the Group
   */
  groupNodes: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Booelean for specifying if current node.
       */
      current: PropTypes.bool,
      /**
       * List of components to be displayed in GroupRow
       */
      nodeComponents: PropTypes.array,
      /**
       * Object contains error messages for incorrect drops,
       * based on position of element
       */
      nodeFeedback: PropTypes.object,
      /**
       * Unique id for the Node
       */
      nodeId: PropTypes.number.isRequired,
      /**
       * The type of node.
       */
      nodeType: PropTypes.oneOf([
        SLIDE_NODE.JOINT_DRAG_DROP,
        SLIDE_NODE.BOX_DRAG_DROP,
      ]).isRequired,
    })
  ),
  /**
   * The hexvalue for the colour associated with the box
   * group
   */
  groupColor: PropTypes.string,
  /**
   * Label String to be always displayed inside the Box
   */
  groupTitle: PropTypes.string,
  /**
   * Boolean specifying if BoxGroup is visited
   */
  visited: PropTypes.bool.isRequired,
  /**
   * DragDrop Row component for FullWidthCard nodes
   */
  rectDragDropRow: PropTypes.element.isRequired,
  /**
   * DragDropRow component for HexagonCard nodes
   */
  jointDragDropRow: PropTypes.element.isRequired,
};

WithoutLabel.defaultProsps = {
  groupId: null,
  groupNodes: [],
  visited: false,
  groupColor: "#ffffff",
};

export const BoxGroup = {
  WithLabel,
  WithoutLabel,
};
